import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

/** Card with a title row (title, subtitle, action) — the standard container for charts and lists. */
export function Panel({
  title,
  subtitle,
  action,
  children,
  padded = true,
  sx,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  padded?: boolean;
  sx?: object;
}) {
  return (
    <Card sx={{ minWidth: 0, ...sx }}>
      {(title ?? action) && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 2.5,
            pt: 2.25,
            pb: padded ? 0 : 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography variant="h5">{title}</Typography>}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      {padded ? <CardContent>{children}</CardContent> : children}
    </Card>
  );
}

export type Tone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

function toneColor(tone: Tone): string {
  return tone === 'neutral'
    ? 'var(--mui-palette-text-secondary)'
    : `var(--mui-palette-${tone}-main)`;
}

/** KPI tile: label, big tabular value, hint, tinted icon. */
export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  loading,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  loading?: boolean;
}) {
  const c = toneColor(tone);
  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
        '&:hover': { boxShadow: '0 12px 24px -6px rgba(16,24,40,0.10)' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -36,
          top: -36,
          width: 130,
          height: 130,
          borderRadius: '50%',
          background: `radial-gradient(circle, color-mix(in srgb, ${c} 14%, transparent), transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <CardContent>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
            {label}
          </Typography>
          {icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '11px',
                display: 'grid',
                placeItems: 'center',
                color: c,
                bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
                '& svg': { fontSize: 19 },
              }}
            >
              {icon}
            </Box>
          )}
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={40} />
        ) : (
          <Typography
            variant="h4"
            className="tabular"
            sx={{ mt: 0.5, fontSize: { xs: '1.2rem', sm: '1.4rem' }, wordBreak: 'break-word' }}
          >
            {value}
          </Typography>
        )}
        {hint && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {loading ? <Skeleton width="40%" /> : hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** A responsive row of KPI tiles. */
export function StatGrid({ children, min = 200 }: { children: ReactNode; min?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr 1fr',
          md: `repeat(auto-fit, minmax(${String(min)}px, 1fr))`,
        },
        mb: 2.5,
        '& > :only-child': { gridColumn: '1 / -1' },
      }}
    >
      {children}
    </Box>
  );
}

/** Pill-style segmented control (interval, ledger, tabs inside panels). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = 'small',
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  size?: 'small' | 'medium';
  label?: string;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={value}
      aria-label={label}
      onChange={(_, v: T | null) => {
        if (v) onChange(v);
      }}
      sx={(t) => ({
        p: 0.375,
        gap: 0.25,
        borderRadius: '11px',
        bgcolor: 'action.hover',
        flexWrap: 'wrap',
        '& .MuiToggleButton-root': {
          border: 0,
          borderRadius: '8px !important',
          px: 1.5,
          py: 0.5,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          '&.Mui-selected': {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 1px 2px rgba(16,24,40,0.08), 0 1px 3px rgba(16,24,40,0.06)',
            '&:hover': { bgcolor: 'background.paper' },
          },
          ...t.applyStyles('dark', {
            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)', boxShadow: 'none' },
          }),
        },
      })}
    >
      {options.map((o) => (
        <ToggleButton key={o.value} value={o.value}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** Label/value pair for detail drawers. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 550, wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
}

/** Pretty-printed JSON for payloads (sync conflicts, raw audit data). */
export function JsonBlock({ value, maxHeight = 320 }: { value: unknown; maxHeight?: number }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        fontSize: 12,
        lineHeight: 1.55,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        overflow: 'auto',
        maxHeight,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {value === undefined || value === null ? '—' : JSON.stringify(value, null, 2)}
    </Box>
  );
}

/** Small round tinted icon used in lists (notifications, devices, rules). */
export function IconBadge({
  icon,
  tone = 'primary',
  size = 40,
}: {
  icon: ReactNode;
  tone?: Tone;
  size?: number;
}) {
  const c = toneColor(tone);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: `${String(Math.round(size * 0.3))}px`,
        display: 'grid',
        placeItems: 'center',
        color: c,
        bgcolor: `color-mix(in srgb, ${c} 11%, transparent)`,
        border: `1px solid color-mix(in srgb, ${c} 16%, transparent)`,
        '& svg': { fontSize: Math.round(size * 0.5) },
      }}
    >
      {icon}
    </Box>
  );
}
